import { Icon } from '../../../../widgets/icon';
import { DropdownActionItem, DropdownActions } from '../../../../widgets/dropdown';
import { ZTooltip } from '../../../../widgets/ztooltip';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import DeleteIcon from 'src/shared/material_icons/action/delete_outline.svg';
import HistoryIcon from 'src/shared/material_icons/action/history.svg';
import ImageIcon from 'src/shared/material_icons/image/image.svg';
import MicrophoneIcon from 'src/shared/material_icons/legacy/microphone-with-gradient.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import styled from 'styled-components';
import { useAIStudioPromptHistory } from '../../hooks/useAIStudioPromptHistory';
import { bounce } from '../../styles/animations';

const InputContainer = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 12px;
    min-height: 50px;
    box-sizing: border-box;
    background: ${(props) => props.theme.app.backgroundColorBase};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 12px;
    transition: border-color 0.2s ease;

    &:focus-within {
        border-color: #2196f3;
    }
`;

const UploadButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border: none;
    border-radius: 8px;
    background: ${(props) => props.theme.app.backgroundColor};
    color: ${(props) => props.theme.app.textColor};
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background: ${(props) => props.theme.app.primary_light};
        color: ${(props) => props.theme.app.primary};
    }

    svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
    }
`;

const HiddenFileInput = styled.input`
    display: none;
`;

const TextInput = styled.textarea`
    flex: 1;
    border: none;
    background: transparent;
    font-size: 14px;
    color: ${(props) => props.theme.app.textColor};
    outline: none;
    resize: none;
    overflow: hidden;
    min-height: 24px;
    max-height: 120px;
    overflow-y: auto;
    line-height: 1.5;
    font-family: inherit;
    padding: 0;
    align-self: center;

    &::placeholder {
        color: ${(props) => props.theme.app.textColor};
        opacity: 0.5;
    }
`;

const UploadedImagePreview = styled.div`
    position: relative;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    overflow: hidden;
    flex-shrink: 0;
`;

const PreviewImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const RemoveButton = styled.button`
    position: absolute;
    top: -4px;
    right: -4px;
    width: 18px;
    height: 18px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #ef4444;
    color: white;
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: #dc2626;
    }
`;

const MicButton = styled.button<{ $isRecording: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 16px;
    background: ${(props) => (props.$isRecording ? 'white' : 'white')};
    color: ${(props) => (props.$isRecording ? '#ffffff' : '#6b7280')};
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background: ${(props) => (props.$isRecording ? '#fdd' : '#eaeaea')};
        color: ${(props) => (props.$isRecording ? '#ffffff' : '#374151')};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    ${Icon} {
        width: 24px;
        height: 24px;
        min-width: 0;
        fill: currentColor;

        svg {
            width: 24px;
            height: 24px;
        }
    }
`;

const PromptActionButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 16px;
    background: white;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background: #eaeaea;
        color: #374151;
    }

    ${Icon} {
        width: 18px;
        height: 18px;
        min-width: 0;
        fill: currentColor;

        svg {
            width: 18px;
            height: 18px;
        }
    }
`;

const HistoryPromptRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 4px;
`;

const HistoryPromptButton = styled.button`
    flex: 1;
    min-width: 0;
    padding: 7px 8px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #263238;
    font: inherit;
    font-size: 13px;
    line-height: 18px;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;

    &:hover {
        background: #f3f4f6;
    }
`;

const RemoveHistoryPromptButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #6b7280;
    cursor: pointer;

    &:hover {
        background: #fee2e2;
        color: #dc2626;
    }

    ${Icon},
    svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

// animated dots components

const SvgDotsWrapper = styled.svg`
    .dot-1 {
        animation: ${bounce} 0.75s ease-in-out infinite;
        animation-delay: 0s;
        transform-origin: center;
    }
    .dot-2 {
        animation: ${bounce} 0.75s ease-in-out infinite;
        animation-delay: 0.15s;
        transform-origin: center;
    }
    .dot-3 {
        animation: ${bounce} 0.75s ease-in-out infinite;
        animation-delay: 0.3s;
        transform-origin: center;
    }
`;

const AnimatedListeningDots: FC = () => (
    <SvgDotsWrapper xmlns='http://www.w3.org/2000/svg' viewBox='0 -960 960 960'>
        <defs>
            <linearGradient id='animated-dots-gradient' x1='160' y1='-480' x2='800' y2='-480' gradientUnits='userSpaceOnUse'>
                <stop stopColor='#3B82F6' />
                <stop offset='1' stopColor='#8B5CF6' />
            </linearGradient>
        </defs>
        <circle className='dot-1' cx='240' cy='-320' r='80' fill='url(#animated-dots-gradient)' />
        <circle className='dot-2' cx='480' cy='-320' r='80' fill='url(#animated-dots-gradient)' />
        <circle className='dot-3' cx='720' cy='-320' r='80' fill='url(#animated-dots-gradient)' />
    </SvgDotsWrapper>
);

// Type declarations for Web Speech API
interface SpeechRecognitionEvent extends Event {
    results: SpeechRecognitionResultList;
    resultIndex: number;
}

interface SpeechRecognitionResultList {
    length: number;
    item(index: number): SpeechRecognitionResult;
    [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
    length: number;
    item(index: number): SpeechRecognitionAlternative;
    [index: number]: SpeechRecognitionAlternative;
    isFinal: boolean;
}

interface SpeechRecognitionAlternative {
    transcript: string;
    confidence: number;
}

interface SpeechRecognition extends EventTarget {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    onresult: ((event: SpeechRecognitionEvent) => void) | null;
    onerror: ((event: Event) => void) | null;
    onend: (() => void) | null;
    start(): void;
    stop(): void;
    abort(): void;
}

interface SpeechRecognitionConstructor {
    new (): SpeechRecognition;
}
declare global {
    interface Window {
        SpeechRecognition?: SpeechRecognitionConstructor;
        webkitSpeechRecognition?: SpeechRecognitionConstructor;
    }
}

interface PromptInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    maxLength?: number;
    uploadedImageUrl?: string;
    onImageUpload?: (file: File) => void;
    onImageRemove?: () => void;
    disabled?: boolean;
}

export const PromptInput: FC<PromptInputProps> = ({
    value,
    onChange,
    placeholder,
    maxLength = 500,
    uploadedImageUrl,
    onImageUpload,
    onImageRemove,
    disabled = false
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [speechSupported, setSpeechSupported] = useState(false);
    const recognitionRef = useRef<SpeechRecognition | null>(null);
    const hasInitializedPromptRef = useRef(false);
    const { prompts: recentPrompts, latestPrompt, removePrompt, clearPrompts } = useAIStudioPromptHistory();

    useEffect(() => {
        if (hasInitializedPromptRef.current || disabled) return;
        hasInitializedPromptRef.current = true;
        if (!value && latestPrompt) onChange(latestPrompt.slice(0, maxLength));
    }, [disabled, latestPrompt, maxLength, onChange, value]);

    // Check if speech recognition is supported
    useEffect(() => {
        const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
        setSpeechSupported(!!SpeechRecognitionAPI);
    }, []);

    // Auto-resize textarea to fit content
    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
    }, [value]);

    const handleUploadClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file && onImageUpload) {
                onImageUpload(file);
            }
            // Reset input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        },
        [onImageUpload]
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);
            const file = e.dataTransfer.files?.[0];
            if (file && file.type.startsWith('image/') && onImageUpload) {
                onImageUpload(file);
            }
        },
        [onImageUpload]
    );

    const startRecording = useCallback(() => {
        const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognitionAPI) return;

        const recognition = new SpeechRecognitionAPI();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = navigator.language || 'en-US';

        recognition.onresult = (event: SpeechRecognitionEvent) => {
            let finalTranscript = '';
            let interimTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i];
                if (result.isFinal) {
                    finalTranscript += result[0].transcript;
                } else {
                    interimTranscript += result[0].transcript;
                }
            }

            if (finalTranscript) {
                const newValue = value + (value ? ' ' : '') + finalTranscript;
                onChange(newValue.slice(0, maxLength));
            }
        };

        recognition.onerror = () => {
            setIsRecording(false);
            recognitionRef.current = null;
        };

        recognition.onend = () => {
            setIsRecording(false);
            recognitionRef.current = null;
        };

        recognitionRef.current = recognition;
        recognition.start();
        setIsRecording(true);
    }, [value, onChange, maxLength]);

    const stopRecording = useCallback(() => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            recognitionRef.current = null;
        }
        setIsRecording(false);
    }, []);

    const toggleRecording = useCallback(() => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    }, [isRecording, startRecording, stopRecording]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.abort();
            }
        };
    }, []);

    return (
        <InputContainer
            style={{ borderColor: isDragOver ? '#2196f3' : undefined }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {onImageUpload && !uploadedImageUrl && (
                <>
                    <UploadButton onClick={handleUploadClick} disabled={disabled} title={T._('Upload image', 'Admin')}>
                        <Icon>
                            <ImageIcon />
                        </Icon>
                    </UploadButton>
                    <HiddenFileInput ref={fileInputRef} type='file' accept='image/*' onChange={handleFileChange} />
                </>
            )}
            {uploadedImageUrl && (
                <UploadedImagePreview>
                    <PreviewImage src={uploadedImageUrl} alt={T._('Uploaded', 'Admin')} />
                    {onImageRemove && (
                        <RemoveButton onClick={onImageRemove} title={T._('Remove image', 'Admin')}>
                            ×
                        </RemoveButton>
                    )}
                </UploadedImagePreview>
            )}
            <TextInput
                ref={textareaRef}
                rows={1}
                value={value}
                onChange={(e) => onChange(e.target.value.slice(0, maxLength))}
                placeholder={placeholder || T._('Describe what you want to create...', 'Admin')}
                disabled={disabled}
            />
            {value && !disabled && (
                <ZTooltip html={T._('Clear prompt', 'Admin')} position='top'>
                    <PromptActionButton type='button' onClick={() => onChange('')} aria-label={T._('Clear prompt', 'Admin')}>
                        <Icon>
                            <CloseIcon />
                        </Icon>
                    </PromptActionButton>
                </ZTooltip>
            )}
            {recentPrompts.length > 0 && !disabled && (
                <DropdownActions
                    placement='top-end'
                    width='340px'
                    minWidth='260px'
                    maxWidth='calc(100vw - 24px)'
                    maxHeight='320px'
                    header={
                        <ZTooltip html={T._('Prompt history', 'Admin')} position='top'>
                            <PromptActionButton type='button' aria-label={T._('Prompt history', 'Admin')}>
                                <Icon>
                                    <HistoryIcon />
                                </Icon>
                            </PromptActionButton>
                        </ZTooltip>
                    }
                    sections={[
                        recentPrompts.map((recentPrompt) => (
                            <HistoryPromptRow key={recentPrompt}>
                                <HistoryPromptButton type='button' title={recentPrompt} onClick={() => onChange(recentPrompt.slice(0, maxLength))}>
                                    {recentPrompt}
                                </HistoryPromptButton>
                                <ZTooltip html={T._('Remove prompt from history', 'Admin')} position='top'>
                                    <RemoveHistoryPromptButton
                                        type='button'
                                        aria-label={T._('Remove prompt from history', 'Admin')}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            removePrompt(recentPrompt);
                                        }}
                                    >
                                        <Icon>
                                            <DeleteIcon />
                                        </Icon>
                                    </RemoveHistoryPromptButton>
                                </ZTooltip>
                            </HistoryPromptRow>
                        )),
                        [
                            <DropdownActionItem
                                key='clear-prompt-history'
                                danger
                                icon={
                                    <Icon>
                                        <DeleteIcon />
                                    </Icon>
                                }
                                onClick={clearPrompts}
                            >
                                {T._('Clear all', 'Admin')}
                            </DropdownActionItem>
                        ]
                    ]}
                />
            )}
            {speechSupported && (
                <MicButton
                    $isRecording={isRecording}
                    onClick={toggleRecording}
                    disabled={disabled}
                    title={isRecording ? T._('Stop recording', 'Admin') : T._('Start voice input', 'Admin')}
                >
                    <Icon>{isRecording ? <AnimatedListeningDots /> : <MicrophoneIcon />}</Icon>
                </MicButton>
            )}
        </InputContainer>
    );
};

export default PromptInput;
